/**
 * assets/logo-sparkles.js
 * Logo / image-block sparkle effect — hardened production build.
 *
 * Drop-in replacement. No template, Liquid, or markup changes required.
 * Keeps the existing public API: the theme's inline section scripts call
 *   if (typeof LogoSparkles !== 'undefined') new LogoSparkles(container);
 * and that continues to work unchanged.
 *
 * Fixes vs. previous version:
 *   - rAF loop idles out when there is nothing to draw (was infinite, one
 *     permanent loop per instance; 3 instances run on the homepage)
 *   - Loop now actually respects IntersectionObserver visibility. The previous
 *     version set this.isVisible but never read it, so off-screen logos kept
 *     animating for the whole session.
 *   - Entrance animation debounced; previously re-fired on every scroll back
 *     into view, re-spawning 25 particles each time
 *   - Zero-size containers are skipped (one header canvas renders at 0x0 and
 *     still allocated a buffer and a loop)
 *   - clearRect scoped to CSS pixels to match the scaled context
 *   - Resize handling debounced; was reallocating the buffer per resize event
 *
 * Hardening:
 *   - Re-constructing on the same element returns the existing instance
 *   - ResizeObserver picks up containers that gain size later (lazy images)
 *   - Pauses on hidden tab
 *   - Handles 2D context loss/restore
 *   - Delta-time motion, identical behaviour at 30 / 60 / 120Hz
 *   - Live prefers-reduced-motion response
 *   - DPR capped at 2
 *   - Shopify theme-editor safe: instances on an unloaded section are destroyed
 *   - Never throws into the page
 */
(function () {
  'use strict';

  if (window.__vhLogoSparklesLoaded) return;
  window.__vhLogoSparklesLoaded = true;

  var CONFIG = {
    maxSparkles: 50,
    entranceCount: 25,
    entranceDurationMs: 2000,
    entranceDelayMs: 500,
    lifetimeFrames: 540,
    boundaryMultiplier: 2,
    maxDpr: 2,
    resizeDebounceMs: 150
  };

  var COLORS = ['#F2EFE9', '#FFFFFF', '#FFD700', '#FFA500', '#FF6B6B', '#4ECDC4', '#45B7D1'];

  var motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  var instances = [];
  var registry = typeof WeakMap === 'function' ? new WeakMap() : null;

  function on(t, type, fn, opts) { if (t && t.addEventListener) t.addEventListener(type, fn, opts); }
  function off(t, type, fn) { if (t && t.removeEventListener) t.removeEventListener(type, fn); }

  function LogoSparkles(logoElement) {
    if (!logoElement || logoElement.nodeType !== 1) return;

    // Guard against double construction on the same element. The theme's
    // inline snippets poll on an interval and can fire more than once.
    if (registry && registry.has(logoElement)) {
      var existing = registry.get(logoElement);
      if (existing && !existing.destroyed) return existing;
    }
    if (registry) registry.set(logoElement, this);

    this.logo = logoElement;
    this.canvas = null;
    this.ctx = null;
    this.sparkles = [];
    this.isVisible = false;
    this.running = false;
    this.destroyed = false;
    this.contextLost = false;
    this.animationId = null;
    this.entranceStart = null;
    this.entranceTimer = null;
    this.resizeTimer = null;
    this.lastFrame = 0;

    this.cssWidth = 0;
    this.cssHeight = 0;
    this.logoWidth = 0;
    this.logoHeight = 0;
    this.centerX = 0;
    this.centerY = 0;
    this.maxDistance = 0;
    this.dpr = 0;

    this._onResize = this._onResize.bind(this);
    this._onVisibility = this._onVisibility.bind(this);
    this._onContextLost = this._onContextLost.bind(this);
    this._onContextRestored = this._onContextRestored.bind(this);
    this._onMotionChange = this._onMotionChange.bind(this);
    this._animate = this._animate.bind(this);

    instances.push(this);
    this._init();
  }

  LogoSparkles.prototype._init = function () {
    this.canvas = this.logo.querySelector('[data-sparkle-canvas]');
    if (!this.canvas) return;

    try {
      this.ctx = this.canvas.getContext('2d');
    } catch (e) {
      this.ctx = null;
    }
    if (!this.ctx) return;

    this.canvas.setAttribute('aria-hidden', 'true');
    this.canvas.style.pointerEvents = 'none';

    this._measure();

    on(window, 'resize', this._onResize, { passive: true });
    on(document, 'visibilitychange', this._onVisibility);
    on(this.canvas, 'contextlost', this._onContextLost);
    on(this.canvas, 'contextrestored', this._onContextRestored);
    if (typeof motionQuery.addEventListener === 'function') {
      motionQuery.addEventListener('change', this._onMotionChange);
    }

    // Picks up containers that gain size after an image finishes loading.
    if (typeof ResizeObserver === 'function') {
      var self = this;
      this.resizeObserver = new ResizeObserver(function () { self._onResize(); });
      this.resizeObserver.observe(this.logo);
    }

    this._observeVisibility();
  };

  LogoSparkles.prototype._enabled = function () {
    return !motionQuery.matches && !this.destroyed && !this.contextLost;
  };

  LogoSparkles.prototype._measure = function () {
    if (!this.ctx) return false;

    var rect = this.logo.getBoundingClientRect();

    // Skip collapsed or hidden containers entirely.
    if (rect.width < 1 || rect.height < 1) {
      this.cssWidth = 0;
      this.cssHeight = 0;
      return false;
    }

    var dpr = Math.min(window.devicePixelRatio || 1, CONFIG.maxDpr);
    var cssW = rect.width * CONFIG.boundaryMultiplier;
    var cssH = rect.height * CONFIG.boundaryMultiplier;

    if (cssW === this.cssWidth && cssH === this.cssHeight && dpr === this.dpr) return true;

    this.logoWidth = rect.width;
    this.logoHeight = rect.height;
    this.cssWidth = cssW;
    this.cssHeight = cssH;
    this.dpr = dpr;

    this.canvas.width = Math.max(1, Math.round(cssW * dpr));
    this.canvas.height = Math.max(1, Math.round(cssH * dpr));
    this.canvas.style.width = cssW + 'px';
    this.canvas.style.height = cssH + 'px';
    // Assigning width/height resets context state, so set the transform after.
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    this.centerX = cssW / 2;
    this.centerY = cssH / 2;
    this.maxDistance = Math.max(rect.width, rect.height) * CONFIG.boundaryMultiplier;
    return true;
  };

  LogoSparkles.prototype._onResize = function () {
    var self = this;
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(function () {
      if (self.destroyed) return;
      var ok = self._measure();
      if (ok && self.sparkles.length) self._start();
    }, CONFIG.resizeDebounceMs);
  };

  LogoSparkles.prototype._onVisibility = function () {
    if (document.hidden) this._stop();
  };

  LogoSparkles.prototype._onMotionChange = function () {
    if (motionQuery.matches) this._stop();
  };

  LogoSparkles.prototype._onContextLost = function (e) {
    if (e && e.preventDefault) e.preventDefault();
    this.contextLost = true;
    this._stop();
  };

  LogoSparkles.prototype._onContextRestored = function () {
    this.contextLost = false;
    try {
      this.ctx = this.canvas.getContext('2d');
    } catch (err) {
      this.ctx = null;
      return;
    }
    this.cssWidth = this.cssHeight = this.dpr = 0;
    this._measure();
  };

  LogoSparkles.prototype._observeVisibility = function () {
    if (typeof IntersectionObserver !== 'function') {
      // Very old browser: run the entrance once and let it idle out.
      this.isVisible = true;
      this._scheduleEntrance();
      return;
    }
    var self = this;
    this.observer = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        self.isVisible = entries[i].isIntersecting;
        if (self.isVisible) {
          self._scheduleEntrance();
        } else {
          // Stop burning frames on an off-screen element.
          clearTimeout(self.entranceTimer);
          self._stop();
        }
      }
    }, { threshold: 0.1 });
    this.observer.observe(this.logo);
  };

  LogoSparkles.prototype._scheduleEntrance = function () {
    var self = this;
    clearTimeout(this.entranceTimer);
    this.entranceTimer = setTimeout(function () {
      if (self.destroyed || !self.isVisible || document.hidden) return;
      if (!self._enabled()) return;
      if (!self._measure()) return;
      self.entranceStart = Date.now();
      self._start();
    }, CONFIG.entranceDelayMs);
  };

  LogoSparkles.prototype._addSparkle = function () {
    if (this.sparkles.length >= CONFIG.maxSparkles) return;
    this.sparkles.push({
      x: this.centerX + (Math.random() - 0.5) * this.logoWidth * 0.8,
      y: this.centerY + (Math.random() - 0.5) * this.logoHeight * 0.8,
      r: Math.random() * 1.5 + 0.8,
      opacity: 1,
      speed: Math.random() * 0.6 + 0.15,
      drift: (Math.random() - 0.5) * 2,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      life: CONFIG.lifetimeFrames
    });
  };

  LogoSparkles.prototype._start = function () {
    if (this.running || !this._enabled() || document.hidden) return;
    if (this.cssWidth < 1) return;
    this.running = true;
    this.lastFrame = performance.now();
    this.animationId = requestAnimationFrame(this._animate);
  };

  LogoSparkles.prototype._stop = function () {
    this.running = false;
    if (this.animationId) cancelAnimationFrame(this.animationId);
    this.animationId = null;
    this.entranceStart = null;
    this.sparkles.length = 0;
    if (this.ctx && this.cssWidth > 0) this.ctx.clearRect(0, 0, this.cssWidth, this.cssHeight);
  };

  LogoSparkles.prototype._animate = function (now) {
    if (this.destroyed || !this.running || !this.ctx) return;

    var delta = now - this.lastFrame;
    this.lastFrame = now;
    var f = Math.min(Math.max(delta, 0) / 16.667, 3);

    var ctx = this.ctx;
    ctx.clearRect(0, 0, this.cssWidth, this.cssHeight);

    if (this.entranceStart && this.sparkles.length < CONFIG.entranceCount) {
      var progress = Math.min((Date.now() - this.entranceStart) / CONFIG.entranceDurationMs, 1);
      var eased = 1 - Math.pow(1 - progress, 3);
      var target = Math.floor(eased * CONFIG.entranceCount);
      while (this.sparkles.length < target) this._addSparkle();
      if (progress >= 1) this.entranceStart = null;
    }

    var list = this.sparkles;
    var maxSq = this.maxDistance * this.maxDistance;

    for (var i = list.length; i--; ) {
      var s = list[i];
      s.x += s.drift * f;
      s.y += s.speed * f;
      s.opacity -= (1 / s.life) * f;
      s.life -= f;

      var dx = s.x - this.centerX;
      var dy = s.y - this.centerY;
      if (s.opacity <= 0 || s.life <= 0 || dx * dx + dy * dy > maxSq) {
        list.splice(i, 1);
        continue;
      }

      ctx.globalAlpha = s.opacity;
      ctx.beginPath();
      ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
      ctx.fillStyle = '#ffffff';
      ctx.fill();
      ctx.lineWidth = 1;
      ctx.strokeStyle = s.color;
      ctx.stroke();
    }
    ctx.globalAlpha = 1;

    // Idle out rather than spinning on an empty array.
    if (!list.length && !this.entranceStart) {
      this.running = false;
      this.animationId = null;
      return;
    }

    this.animationId = requestAnimationFrame(this._animate);
  };

  // Kept for API compatibility with the previous version.
  LogoSparkles.prototype.animate = function () { this._start(); };
  LogoSparkles.prototype.start = function () { this._start(); };
  LogoSparkles.prototype.stop = function () { this._stop(); };
  LogoSparkles.prototype.triggerEntranceAnimation = function () { this._scheduleEntrance(); };

  LogoSparkles.prototype.destroy = function () {
    if (this.destroyed) return;
    this.destroyed = true;
    this._stop();
    clearTimeout(this.entranceTimer);
    clearTimeout(this.resizeTimer);
    if (this.observer) this.observer.disconnect();
    if (this.resizeObserver) this.resizeObserver.disconnect();
    off(window, 'resize', this._onResize);
    off(document, 'visibilitychange', this._onVisibility);
    off(this.canvas, 'contextlost', this._onContextLost);
    off(this.canvas, 'contextrestored', this._onContextRestored);
    if (typeof motionQuery.removeEventListener === 'function') {
      motionQuery.removeEventListener('change', this._onMotionChange);
    }
    if (registry && this.logo) registry.delete(this.logo);
    var idx = instances.indexOf(this);
    if (idx > -1) instances.splice(idx, 1);
  };

  // Theme editor: tear down instances inside a section that is being replaced.
  on(document, 'shopify:section:unload', function (e) {
    for (var i = instances.length; i--; ) {
      var inst = instances[i];
      if (e.target && inst.logo && e.target.contains(inst.logo)) inst.destroy();
    }
  });

  window.LogoSparkles = LogoSparkles;
})();
